import { Dropdown, Menu, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseRange, createEditor, Editor, Range, Transforms } from 'slate';
import { withHistory } from 'slate-history';
import { ReactEditor, Slate, useFocused, useSelected, withReact } from 'slate-react';
import styled, { css } from 'styled-components';

import RichTextDataInputSvg from '../../../../assets/icons/RichtTextDataInputSvg';
import theme from '../../../../assets/theme';
import { LocalPromptDependency } from '../../../../services/backendService/types';
import { zIndexes } from '../../../../utils/zIndexes';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import {
  Attributes,
  CuripodDecendant,
  CuripodElement,
  CuripodPromptInputElement,
  CuripodPromptLiveElement,
  CuripodPromptOutputElement,
  CuripodText,
  Leaf,
  parseSlateStringToText,
  parseStringToSlateObject,
  SlateEditable,
} from '../../../SlideComponents/slateUtil';

const withPromptInputsAndOutputs = (editor: Editor) => {
  const { isInline, isVoid, markableVoid } = editor;

  editor.isInline = element => {
    return element.type === 'promptInput' ||
      element.type === 'promptOutput' ||
      element.type === 'livePromptInput'
      ? true
      : isInline(element);
  };

  editor.isVoid = element => {
    return element.type === 'promptInput' ||
      element.type === 'promptOutput' ||
      element.type === 'livePromptInput'
      ? true
      : isVoid(element);
  };

  editor.markableVoid = element => {
    return (
      element.type === 'promptInput' ||
      element.type === 'promptOutput' ||
      element.type === 'livePromptInput' ||
      markableVoid(element)
    );
  };

  return editor;
};

const Element = ({
  props,
  availablePromptDependencies,
}: {
  props: {
    element: CuripodElement;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: any;
    attributes: Attributes;
  };
  availablePromptDependencies: LocalPromptDependency[];
}) => {
  const { attributes, children, element } = props;

  const isAvailableDependency = useMemo(
    () =>
      availablePromptDependencies.some(
        d =>
          (element as CuripodPromptInputElement).inputId === d.id ||
          (element as CuripodPromptOutputElement).outputId === d.id,
      ),
    [availablePromptDependencies, element],
  );

  switch (element.type) {
    case 'promptInput': {
      //@ts-ignore
      return (
        <PromptInputElement
          attributes={props.attributes}
          element={element as CuripodPromptInputElement}
          isAvailableDependency={isAvailableDependency}
        >
          {children}
        </PromptInputElement>
      );
    }
    case 'promptOutput': {
      //@ts-ignore
      return (
        <PromptOutputElement
          attributes={props.attributes}
          element={element as CuripodPromptOutputElement}
          isAvailableDependency={isAvailableDependency}
        >
          {children}
        </PromptOutputElement>
      );
    }
    case 'livePromptInput': {
      //@ts-ignore
      return (
        <PromptLiveElement
          attributes={props.attributes}
          element={element as CuripodPromptOutputElement}
          isAvailableDependency={isAvailableDependency}
        >
          {children}
        </PromptLiveElement>
      );
    }
    default:
      return <p {...attributes}>{children}</p>;
  }
};

const PromptInputElement = ({
  attributes,
  children,
  element,
  isAvailableDependency,
}: {
  element: CuripodPromptInputElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  attributes: Attributes;
  isAvailableDependency: boolean;
}) => {
  const selected = useSelected();
  const focused = useFocused();
  const style: React.CSSProperties = {
    padding: '3px 3px 2px',
    margin: '0 1px',
    verticalAlign: 'baseline',
    borderRadius: '4px',
    backgroundColor: '#FFA55B',
    fontSize: '0.9em',
    color: 'white',
    boxShadow: selected && focused ? '0 0 0 2px #B4D5FF' : 'none',
    cursor: 'pointer',
  };
  if (!isAvailableDependency) {
    return (
      <Tooltip title={'Not an available input'}>
        <StyledSpan
          {...attributes}
          contentEditable={false}
          style={{ ...style, backgroundColor: theme.colors.red }}
        >
          <RichTextDataInputSvg />
          {element.label}
          {children}
        </StyledSpan>
      </Tooltip>
    );
  }

  if (element.label === 'Grade') {
    return (
      <Tooltip title={'e.g "5th"'}>
        <StyledSpan {...attributes} contentEditable={false} style={style}>
          <RichTextDataInputSvg />
          {element.label}
          {children}
        </StyledSpan>
      </Tooltip>
    );
  }
  return (
    <StyledSpan {...attributes} contentEditable={false} style={style}>
      <RichTextDataInputSvg />
      {element.label}
      {children}
    </StyledSpan>
  );
};

const StyledSpan = styled.span`
  display: inline-flex;
  align-items: center;
  svg {
    margin-right: 2px;
    width: 16px;
    height: 16px;
  }
`;

const PromptOutputElement = ({
  attributes,
  children,
  element,
  isAvailableDependency,
}: {
  element: CuripodPromptOutputElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  attributes: Attributes;
  isAvailableDependency: boolean;
}) => {
  const selected = useSelected();
  const focused = useFocused();

  const style: React.CSSProperties = {
    padding: '3px 3px 2px',
    margin: '0 1px',
    verticalAlign: 'baseline',
    borderRadius: '4px',
    backgroundColor: '#33A0DD',
    fontSize: '0.9em',
    color: 'white',
    boxShadow: selected && focused ? '0 0 0 2px #B4D5FF' : 'none',
    cursor: 'pointer',
  };

  if (!isAvailableDependency) {
    return (
      <Tooltip title={'Not an available output'}>
        <StyledSpan
          {...attributes}
          contentEditable={false}
          style={{ ...style, backgroundColor: theme.colors.red }}
        >
          <RichTextDataInputSvg />
          {element.label}
          {children}
        </StyledSpan>
      </Tooltip>
    );
  }

  return (
    <StyledSpan {...attributes} contentEditable={false} style={style}>
      <RichTextDataInputSvg />
      {element.label}
      {children}
    </StyledSpan>
  );
};

const PromptLiveElement = ({
  attributes,
  children,
  element,
  isAvailableDependency,
}: {
  element: CuripodPromptOutputElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  attributes: Attributes;
  isAvailableDependency: boolean;
}) => {
  const selected = useSelected();
  const focused = useFocused();
  const style: React.CSSProperties = {
    padding: '3px 3px 2px',
    margin: '0 1px',
    verticalAlign: 'baseline',
    borderRadius: '4px',
    backgroundColor: '#139982',
    fontSize: '0.9em',
    color: 'white',
    boxShadow: selected && focused ? '0 0 0 2px #419386' : 'none',
    cursor: 'pointer',
  };

  if (!isAvailableDependency) {
    return (
      <Tooltip title={'Not an available output'}>
        <StyledSpan
          {...attributes}
          contentEditable={false}
          style={{ ...style, backgroundColor: theme.colors.red }}
        >
          <RichTextDataInputSvg />
          {element.label}
          {children}
        </StyledSpan>
      </Tooltip>
    );
  }

  return (
    <StyledSpan {...attributes} contentEditable={false} style={style}>
      <RichTextDataInputSvg />
      {element.label}
      {children}
    </StyledSpan>
  );
};

const insertPromptInput = (
  editor: Editor,
  inputId: string,
  label: string,
  type: 'input' | 'output' | 'live',
) => {
  if (type === 'input') {
    const element: CuripodPromptInputElement = {
      type: 'promptInput',
      inputId,
      label,
      children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, element);
  } else if (type === 'output') {
    const element: CuripodPromptOutputElement = {
      type: 'promptOutput',
      outputId: inputId,
      label,
      children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, element);
  } else if (type === 'live') {
    const element: CuripodPromptLiveElement = {
      type: 'livePromptInput',
      inputId,
      label,
      children: [{ text: '' }],
    };
    Transforms.insertNodes(editor, element);
  }
  Transforms.move(editor);
};

export const Portal = ({ children }: { children: any }) => {
  return typeof document === 'object' ? createPortal(children, document.body) : null;
};

export const PromptTextEditor = ({
  availablePromptDependencies,
  onChange,
  value,
  onBlur,
  placeholder,
  promptTemplate,
  style,
  slideId,
  maxLength,
}: {
  onChange: (val: string) => void;
  value?: string | null;
  placeholder?: string | null;
  availablePromptDependencies: LocalPromptDependency[];
  onBlur?: () => void;
  promptTemplate?: string;
  style?: React.CSSProperties;
  slideId?: string;
  maxLength?: number;
}) => {
  const ref = useRef<HTMLDivElement | null>(null); //Portal ref
  const [content, setContent] = useState<CuripodElement[] | undefined>();
  const [error, setError] = useState<string | undefined>(undefined);
  useEffect(() => {
    const newContent: CuripodElement[] | undefined = parseStringToSlateObject(value);
    setContent(newContent);
  }, [value]);

  const editor = useMemo(
    () => withPromptInputsAndOutputs(withHistory(withReact(createEditor()))),
    [],
  );

  useEffect(() => {
    Transforms.deselect(editor); // Prevent offset bug in view
    const loadedContent = parseStringToSlateObject(value);
    const contentToLoad = loadedContent.map(content => ({ ...content }));
    editor.children = contentToLoad;
    editor.onChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptTemplate, slideId]);

  const handleChange = useCallback(
    (value: CuripodDecendant[]) => {
      const change = JSON.stringify(value.filter(e => !!e));
      const parsedChange = parseSlateStringToText(change);
      if (maxLength && parsedChange.length >= maxLength) return;

      onChange(change);
    },
    [maxLength, onChange],
  );

  const [target, setTarget] = useState<Range | undefined | null>();
  const [selectedPromptDependencyIndex, setSelectedPromptDependencyIndex] = useState(0);
  const [search, setSearch] = useState('');

  const matchedPromptDependencies = useMemo(() => {
    if (!search) return availablePromptDependencies;
    if (search === '' || search.length < 2) return availablePromptDependencies;
    const match = availablePromptDependencies.filter(p => {
      return p.label.toLowerCase().startsWith(search.toLowerCase());
    });
    //TODO: make sure you can add new ones by just writing the name
    return match;
  }, [availablePromptDependencies, search]);

  const renderElement = useCallback(
    (props: any) => (
      <Element props={props} availablePromptDependencies={availablePromptDependencies} />
    ),
    [],
  );
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  const onKeyDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      // make sure the user dont write more than the max length
      const parsedValue = parseSlateStringToText(value);
      if (maxLength && parsedValue.length >= maxLength) {
        if (event.key !== 'Backspace' && event.key !== 'Delete') {
          event.preventDefault();
          setError(`Max length is ${maxLength} character`);
          return;
        }
      }
      setError(undefined);
      if (target && matchedPromptDependencies.length > 0) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            const prevIndex =
              selectedPromptDependencyIndex >= matchedPromptDependencies.length - 1
                ? 0
                : selectedPromptDependencyIndex + 1;
            setSelectedPromptDependencyIndex(prevIndex);
            break;
          case 'ArrowUp':
            event.preventDefault();
            const nextIndex =
              selectedPromptDependencyIndex <= 0
                ? matchedPromptDependencies.length - 1
                : selectedPromptDependencyIndex - 1;
            setSelectedPromptDependencyIndex(nextIndex);
            break;
          case 'Tab':
          case 'Enter':
            event.preventDefault();
            Transforms.select(editor, target);
            insertPromptInput(
              editor,
              matchedPromptDependencies[selectedPromptDependencyIndex]?.id,
              matchedPromptDependencies[selectedPromptDependencyIndex]?.label,
              matchedPromptDependencies[selectedPromptDependencyIndex]?.type,
            );
            setTarget(null);
            break;
          case 'Escape':
            event.preventDefault();
            setTarget(null);
            break;
        }
      }
    },
    [
      value,
      maxLength,
      target,
      matchedPromptDependencies,
      selectedPromptDependencyIndex,
      editor,
    ],
  );

  useEffect(() => {
    if (target && availablePromptDependencies.length > 0) {
      const el = ref.current;
      if (!el) return;
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.top + window.pageYOffset + 24}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [
    availablePromptDependencies.length,
    editor,
    selectedPromptDependencyIndex,
    search,
    target,
  ]);
  const handleDropdownDependencyClicked = useCallback(
    (dependencyId: string) => {
      const dependency = availablePromptDependencies.find(d => d.id === dependencyId);
      if (!dependency) return;
      insertPromptInput(editor, dependency.id, dependency.label, dependency.type);
      Editor.normalize(editor, { force: true });
    },
    [availablePromptDependencies, editor],
  );
  const inputDependencyMenu = useMemo(
    () => (
      <Menu style={{ padding: '5px', maxHeight: 200, overflowY: 'auto' }}>
        {availablePromptDependencies.map((p, i) => (
          <DependencyItem
            key={p.id}
            onClick={() => {
              handleDropdownDependencyClicked(p.id);
            }}
          >
            <DependencyItemContent
              style={{
                backgroundColor:
                  p.type === 'input'
                    ? '#FFA55B'
                    : p.type === 'live'
                    ? '#139982'
                    : '#33A0DD',
              }}
            >
              <IconWrapper style={{ marginRight: '5px' }}>
                {p.type === 'input' ? (
                  <RichTextDataInputSvg />
                ) : p.type === 'live' ? (
                  <RichTextDataInputSvg />
                ) : (
                  <RichTextDataInputSvg />
                )}
              </IconWrapper>
              {p.label}
            </DependencyItemContent>
          </DependencyItem>
        ))}
      </Menu>
    ),
    [availablePromptDependencies, handleDropdownDependencyClicked],
  );
  if (!content) return null;

  return (
    <span translate="no">
      <Slate
        editor={editor}
        value={content}
        onChange={value => {
          const { selection } = editor;

          if (selection && Range.isCollapsed(selection)) {
            const [start] = Range.edges(selection);

            const wordBefore = Editor.before(editor, start, { unit: 'word' });

            const before = wordBefore && Editor.before(editor, wordBefore);
            const beforeRange = before && Editor.range(editor, before, start);
            const beforeText = beforeRange && Editor.string(editor, beforeRange);

            const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);

            const after = Editor.after(editor, start);
            const afterRange = Editor.range(editor, start, after);
            const afterText = Editor.string(editor, afterRange);
            const afterMatch = afterText.match(/^(\s|$)/);

            const { anchor } = selection;
            // Get the text node and path at the current selection
            const [node, path] = Editor.node(editor, anchor.path);

            // Assuming the node is a text node, extract its text content
            const text = (node as CuripodText).text;
            const characterAtCursor = text[anchor.offset - 1];
            if (characterAtCursor === '@') {
              const atRange: BaseRange = {
                anchor: { path, offset: anchor.offset - 1 },
                focus: { path, offset: anchor.offset },
              };

              setTarget(atRange);
              setSearch('');
              return;
            }

            if (beforeMatch && afterMatch) {
              setTarget(beforeRange);
              setSearch(beforeMatch[1]);

              setSelectedPromptDependencyIndex(0);
              return;
            }
          }

          setTarget(null);
          handleChange(value);
        }}
      >
        <OptionsContainer>
          <Tooltip
            title={
              'Add AI field to prompt. This field is a placeholder and will be substituted when needed. Can also open the meny by typing @'
            }
            mouseEnterDelay={1}
          >
            <OptionDropdownContainer>
              <Dropdown
                trigger={['click']}
                overlay={inputDependencyMenu}
                placement="bottom"
              >
                <IconWrapper color={theme.colors.black} height={'16px'} width={'16px'}>
                  <RichTextDataInputSvg />
                </IconWrapper>
              </Dropdown>
            </OptionDropdownContainer>
          </Tooltip>
        </OptionsContainer>
        {error && <span style={{ color: theme.colors.red }}>{error}</span>}
        <ErrorContainer $showError={!!error}>
          <SlateEditable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            placeholder={placeholder ? placeholder : 'Make me a...'}
            style={{
              background: '#F3F3F1',

              borderRadius: 0,
              padding: 10,
              ...(style || {}),
            }}
            onBlur={onBlur}
          />
        </ErrorContainer>
        {target && availablePromptDependencies.length > 0 && (
          <Portal>
            <div
              ref={ref}
              style={{
                top: '-9999px',
                left: '-9999px',
                position: 'absolute',
                zIndex: zIndexes('MENTION_SLATE_LIST'),
                background: 'white',
                borderRadius: '4px',
                boxShadow: '0 1px 5px rgba(0,0,0,.2)',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {matchedPromptDependencies.map((p, i) => (
                <PromptDependencyListItem
                  key={p.id}
                  onClick={() => {
                    Transforms.select(editor, target);
                    insertPromptInput(editor, p.id, p.label, p.type);
                    setTarget(null);
                  }}
                  style={{
                    background:
                      i === selectedPromptDependencyIndex ? '#ebecf0' : 'transparent',
                  }}
                  onMouseEnter={() => setSelectedPromptDependencyIndex(i)}
                >
                  <span
                    style={{
                      opacity: i === selectedPromptDependencyIndex ? 0.7 : 1,
                      borderRadius: '5px',
                      display: 'flex',
                      width: 'fit-content',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      background:
                        p.type === 'input'
                          ? '#FFA55B'
                          : p.type === 'live'
                          ? '#139982'
                          : '#33A0DD',
                      padding: '1px 5px',
                    }}
                  >
                    <IconWrapper style={{ marginRight: '5px' }}>
                      {p.type === 'input' ? (
                        <RichTextDataInputSvg />
                      ) : p.type === 'live' ? (
                        <RichTextDataInputSvg />
                      ) : (
                        <RichTextDataInputSvg />
                      )}
                    </IconWrapper>
                    {p.label}
                  </span>
                </PromptDependencyListItem>
              ))}
            </div>
          </Portal>
        )}
      </Slate>
    </span>
  );
};
export const PromptTextEditorV2 = ({
  availablePromptDependencies,
  onChange,
  value,
  onBlur,
  placeholder,
  promptTemplate,
  style,
  slideId,
  maxLength,
}: {
  onChange: (val: string) => void;
  value?: string | null;
  placeholder?: string | null;
  availablePromptDependencies: LocalPromptDependency[];
  onBlur?: () => void;
  promptTemplate?: string;
  style?: React.CSSProperties;
  slideId?: string;
  maxLength?: number;
}) => {
  const ref = useRef<HTMLDivElement | null>(null); //Portal ref
  const [content, setContent] = useState<CuripodElement[] | undefined>();
  const [error, setError] = useState<string | undefined>(undefined);
  const editor = useMemo(
    () => withPromptInputsAndOutputs(withHistory(withReact(createEditor()))),
    [],
  );
  useEffect(() => {
    const newContent: CuripodElement[] | undefined = parseStringToSlateObject(value);
    setContent(newContent);
    editor.children = newContent;
    editor.onChange();
  }, [value, editor]);

  useEffect(() => {
    Transforms.deselect(editor); // Prevent offset bug in view
    const loadedContent = parseStringToSlateObject(value);
    const contentToLoad = loadedContent.map(content => ({ ...content }));
    editor.children = contentToLoad;
    editor.onChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptTemplate, slideId]);

  const handleChange = useCallback(
    (value: CuripodDecendant[]) => {
      const change = JSON.stringify(value.filter(e => !!e));
      const parsedChange = parseSlateStringToText(change);
      if (maxLength && parsedChange.length >= maxLength) return;

      onChange(change);
    },
    [maxLength, onChange],
  );

  const [target, setTarget] = useState<Range | undefined | null>();
  const [selectedPromptDependencyIndex, setSelectedPromptDependencyIndex] = useState(0);
  const [search, setSearch] = useState('');

  const matchedPromptDependencies = useMemo(() => {
    if (!search) return availablePromptDependencies;
    if (search === '' || search.length < 2) return availablePromptDependencies;
    const match = availablePromptDependencies.filter(p => {
      return p.label.toLowerCase().startsWith(search.toLowerCase());
    });
    //TODO: make sure you can add new ones by just writing the name
    return match;
  }, [availablePromptDependencies, search]);

  const renderElement = useCallback(
    (props: any) => (
      <Element props={props} availablePromptDependencies={availablePromptDependencies} />
    ),
    [],
  );
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  const onKeyDown = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      // make sure the user dont write more than the max length
      const parsedValue = parseSlateStringToText(value);
      if (maxLength && parsedValue.length >= maxLength) {
        if (event.key !== 'Backspace' && event.key !== 'Delete') {
          event.preventDefault();
          setError(`Max length is ${maxLength} character`);
          return;
        }
      }
      setError(undefined);
      if (target && matchedPromptDependencies.length > 0) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            const prevIndex =
              selectedPromptDependencyIndex >= matchedPromptDependencies.length - 1
                ? 0
                : selectedPromptDependencyIndex + 1;
            setSelectedPromptDependencyIndex(prevIndex);
            break;
          case 'ArrowUp':
            event.preventDefault();
            const nextIndex =
              selectedPromptDependencyIndex <= 0
                ? matchedPromptDependencies.length - 1
                : selectedPromptDependencyIndex - 1;
            setSelectedPromptDependencyIndex(nextIndex);
            break;
          case 'Tab':
          case 'Enter':
            event.preventDefault();
            Transforms.select(editor, target);
            insertPromptInput(
              editor,
              matchedPromptDependencies[selectedPromptDependencyIndex]?.id,
              matchedPromptDependencies[selectedPromptDependencyIndex]?.label,
              matchedPromptDependencies[selectedPromptDependencyIndex]?.type,
            );
            setTarget(null);
            break;
          case 'Escape':
            event.preventDefault();
            setTarget(null);
            break;
        }
      }
    },
    [
      value,
      maxLength,
      target,
      matchedPromptDependencies,
      selectedPromptDependencyIndex,
      editor,
    ],
  );

  useEffect(() => {
    if (target && availablePromptDependencies.length > 0) {
      const el = ref.current;
      if (!el) return;
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      el.style.top = `${rect.top + window.pageYOffset + 24}px`;
      el.style.left = `${rect.left + window.pageXOffset}px`;
    }
  }, [
    availablePromptDependencies.length,
    editor,
    selectedPromptDependencyIndex,
    search,
    target,
  ]);
  const handleDropdownDependencyClicked = useCallback(
    (dependencyId: string) => {
      const dependency = availablePromptDependencies.find(d => d.id === dependencyId);
      if (!dependency) return;
      insertPromptInput(editor, dependency.id, dependency.label, dependency.type);
      Editor.normalize(editor, { force: true });
    },
    [availablePromptDependencies, editor],
  );
  const inputDependencyMenu = useMemo(
    () => (
      <Menu style={{ padding: '5px', maxHeight: 200, overflowY: 'auto' }}>
        {availablePromptDependencies.map((p, i) => (
          <DependencyItem
            key={p.id}
            onClick={() => {
              handleDropdownDependencyClicked(p.id);
            }}
          >
            <DependencyItemContent
              style={{
                backgroundColor:
                  p.type === 'input'
                    ? '#FFA55B'
                    : p.type === 'live'
                    ? '#139982'
                    : '#33A0DD',
              }}
            >
              <IconWrapper style={{ marginRight: '5px' }}>
                {p.type === 'input' ? (
                  <RichTextDataInputSvg />
                ) : p.type === 'live' ? (
                  <RichTextDataInputSvg />
                ) : (
                  <RichTextDataInputSvg />
                )}
              </IconWrapper>
              {p.label}
            </DependencyItemContent>
          </DependencyItem>
        ))}
      </Menu>
    ),
    [availablePromptDependencies, handleDropdownDependencyClicked],
  );
  if (!content) return null;

  return (
    <span translate="no">
      <Slate
        editor={editor}
        value={content}
        onChange={value => {
          const { selection } = editor;

          if (selection && Range.isCollapsed(selection)) {
            const [start] = Range.edges(selection);

            const wordBefore = Editor.before(editor, start, { unit: 'word' });

            const before = wordBefore && Editor.before(editor, wordBefore);
            const beforeRange = before && Editor.range(editor, before, start);
            const beforeText = beforeRange && Editor.string(editor, beforeRange);

            const beforeMatch = beforeText && beforeText.match(/^@(\w+)$/);

            const after = Editor.after(editor, start);
            const afterRange = Editor.range(editor, start, after);
            const afterText = Editor.string(editor, afterRange);
            const afterMatch = afterText.match(/^(\s|$)/);

            const { anchor } = selection;
            // Get the text node and path at the current selection
            const [node, path] = Editor.node(editor, anchor.path);

            // Assuming the node is a text node, extract its text content
            const text = (node as CuripodText).text;
            const characterAtCursor = text[anchor.offset - 1];
            if (characterAtCursor === '@') {
              const atRange: BaseRange = {
                anchor: { path, offset: anchor.offset - 1 },
                focus: { path, offset: anchor.offset },
              };

              setTarget(atRange);
              setSearch('');
              return;
            }

            if (beforeMatch && afterMatch) {
              setTarget(beforeRange);
              setSearch(beforeMatch[1]);

              setSelectedPromptDependencyIndex(0);
              return;
            }
          }

          setTarget(null);
          handleChange(value);
        }}
      >
        <OptionsContainer>
          <Tooltip
            title={
              'Add AI field to prompt. This field is a placeholder and will be substituted when needed. Can also open the meny by typing @'
            }
            mouseEnterDelay={1}
          >
            <OptionDropdownContainer>
              <Dropdown
                trigger={['click']}
                overlay={inputDependencyMenu}
                placement="bottom"
              >
                <IconWrapper color={theme.colors.black} height={'16px'} width={'16px'}>
                  <RichTextDataInputSvg />
                </IconWrapper>
              </Dropdown>
            </OptionDropdownContainer>
          </Tooltip>
        </OptionsContainer>
        {error && <span style={{ color: theme.colors.red }}>{error}</span>}
        <ErrorContainer $showError={!!error}>
          <SlateEditable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            onKeyDown={onKeyDown}
            maxLength={maxLength}
            placeholder={placeholder ? placeholder : 'Make me a...'}
            style={{
              background: '#F3F3F1',

              borderRadius: 0,
              padding: 10,
              ...(style || {}),
            }}
            onBlur={onBlur}
          />
        </ErrorContainer>
        {target && availablePromptDependencies.length > 0 && (
          <Portal>
            <div
              ref={ref}
              style={{
                top: '-9999px',
                left: '-9999px',
                position: 'absolute',
                zIndex: zIndexes('MENTION_SLATE_LIST'),
                background: 'white',
                borderRadius: '4px',
                boxShadow: '0 1px 5px rgba(0,0,0,.2)',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {matchedPromptDependencies.map((p, i) => (
                <PromptDependencyListItem
                  key={p.id}
                  onClick={() => {
                    Transforms.select(editor, target);
                    insertPromptInput(editor, p.id, p.label, p.type);
                    setTarget(null);
                  }}
                  style={{
                    background:
                      i === selectedPromptDependencyIndex ? '#ebecf0' : 'transparent',
                  }}
                  onMouseEnter={() => setSelectedPromptDependencyIndex(i)}
                >
                  <span
                    style={{
                      opacity: i === selectedPromptDependencyIndex ? 0.7 : 1,
                      borderRadius: '5px',
                      display: 'flex',
                      width: 'fit-content',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      background:
                        p.type === 'input'
                          ? '#FFA55B'
                          : p.type === 'live'
                          ? '#139982'
                          : '#33A0DD',
                      padding: '1px 5px',
                    }}
                  >
                    <IconWrapper style={{ marginRight: '5px' }}>
                      {p.type === 'input' ? (
                        <RichTextDataInputSvg />
                      ) : p.type === 'live' ? (
                        <RichTextDataInputSvg />
                      ) : (
                        <RichTextDataInputSvg />
                      )}
                    </IconWrapper>
                    {p.label}
                  </span>
                </PromptDependencyListItem>
              ))}
            </div>
          </Portal>
        )}
      </Slate>
    </span>
  );
};

const PromptDependencyListItem = styled.div`
  &:hover {
    cursor: pointer;
  }
  color: white;
  padding: 5px;
`;

const OptionsContainer = styled.div`
  position: relative;
  transform: translateY(-50%);
  width: 100%;

  display: flex;
  justify-content: flex-end;
  z-index: ${zIndexes('SLATE_OPTION_CONTAINER')};
`;
const OptionDropdownContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  transform: translateY(-120%);
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: 10px;

  padding: 5px;

  z-index: 9990;
`;

const DependencyItem = styled.div`
  color: ${theme.colors.white};
  font-weight: bold;
  cursor: pointer;
  padding: 5px;

  &:hover {
    opacity: 0.7;
  }
`;

const DependencyItemContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;
  padding: 1px 5px;
  border-radius: 5px;
`;
const ErrorContainer = styled.div<{ $showError: boolean }>`
  ${({ $showError }) =>
    $showError
      ? css`
          border: 1px solid ${theme.colors.red};
        `
      : ''}
`;
